"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

interface Stat {
  value: number;
  suffix: string;
  label: string;
  description: string;
}

const stats: Stat[] = [
{ value: 89, suffix: "%", label: "Care Gap in Uganda", description: "of Ugandans needing palliative care currently go without it — Beecon exists to change this." },
{ value: 100, suffix: "%", label: "Trained Nurses", description: "All our nurses hold diplomas in Clinical Palliative Care from accredited Ugandan institutions." },
{ value: 1, suffix: ":1", label: "Nurse-to-Patient", description: "One dedicated nurse per patient — not shared, not rotated without your knowledge." },
{ value: 24, suffix: "h", label: "Reporting Cycle", description: "Detailed vital signs reports delivered every 24 hours for doctor review and family peace of mind." }];


const promises = [
{ icon: "ShieldCheckIcon", title: "Clinically Qualified", text: "Every nurse holds a Diploma in Clinical Palliative Care and is registered with Uganda\'s nursing council." },
{ icon: "HomeIcon", title: "Truly Home-Based", text: "We bring the full clinical environment to your home — equipment, medications, monitoring, and expertise." },
{ icon: "DocumentTextIcon", title: "Doctor-Ready Reports", text: "Detailed daily logs of vitals, medications, and patient condition — ready for every medical review." },
{ icon: "PhoneIcon", title: "Family Communication", text: "We keep families informed at every step. No surprises, no uncertainty — just clear, caring updates." }];


function Counter({ target, suffix }: {target: number;suffix: string;}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasRun.current) {
          hasRun.current = true;
          const duration = 1800;
          const steps = 60;
          const increment = target / steps;
          let current = 0;
          const timer = setInterval(() => {
            current = Math.min(current + increment, target);
            setCount(Math.round(current));
            if (current >= target) clearInterval(timer);
          }, duration / steps);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{count}{suffix}</span>;
}

export default function WhyBeeconSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            entry.target.classList.remove('hidden-init');
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal-up');
    els?.forEach((el) => {el.classList.add('hidden-init');observer.observe(el);});
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="navy-section py-24 md:py-32"
      aria-labelledby="why-beecon-heading">
      
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 reveal-up stagger-1">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase font-sans mb-4 px-4 py-2 rounded-full" style={{ color: 'var(--gold)', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)' }}>
            Why Choose Beecon
          </span>
          <h2 id="why-beecon-heading" className="font-serif text-4xl md:text-5xl font-light text-white leading-tight mt-4 mb-5">
            The Most Important Part of Care{" "}
            <br className="hidden md:block" />
            is{" "}
            <span className="italic gold-text">Love and Presence</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto leading-relaxed font-sans" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Uganda is the only African country at the advanced stage of palliative care development. Beecon brings that excellence directly to your door.
          </p>
        </div>

        {/* Asymmetric layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Image side */}
          <div className="relative reveal-up stagger-2">
            <div className="rounded-3xl overflow-hidden aspect-[4/3] relative">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_11bc0c1dc-1769194282259.png"
                alt="Black African woman nurse providing qualified compassionate care to elderly patient at home in Uganda"
                fill
                className="object-cover" />
              
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,31,68,0.3) 0%, transparent 60%)' }} />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-6 -right-6 glass-card-dark p-5 max-w-xs hidden md:block">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.2)' }}>
                  <Icon name="StarIcon" size={20} variant="solid" className="text-gold" style={{ color: 'var(--gold)' }} />
                </div>
                <div>
                  <div className="text-white font-serif text-lg font-light leading-snug">Uganda&apos;s #1 Ranked</div>
                  <div className="text-xs font-sans mt-1" style={{ color: 'rgba(201,168,76,0.7)' }}>
                    Africa&apos;s only country at advanced palliative care stage (WHO, 2025)
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats side */}
          <div className="reveal-up stagger-3">
            <div className="grid grid-cols-2 gap-5 mb-8">
              {stats.map((stat) =>
              <div
                key={`stat-${stat.label}`}
                className="glass-card p-5">
                
                  <div className="font-serif text-3xl md:text-4xl font-light leading-none mb-2" style={{ color: 'var(--gold)' }}>
                    <Counter target={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-white text-sm font-semibold font-sans mb-1">{stat.label}</div>
                  <div className="text-xs leading-relaxed font-sans" style={{ color: 'rgba(255,255,255,0.45)' }}>{stat.description}</div>
                </div>
              )}
            </div>

            <div className="gold-divider mb-8" />

            <blockquote className="font-serif text-xl italic font-light leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
              &ldquo;The most important part of caring for the elderly, the sick, or depressed is to love them and keep them active.&rdquo;
            </blockquote>
            <p className="text-sm font-sans mt-3" style={{ color: 'rgba(201,168,76,0.7)' }}>— Beecon Care Philosophy</p>
          </div>
        </div>

        {/* Promises grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {promises.map((promise, i) =>
          <div
            key={`promise-${promise.title}`}
            className={`glass-card p-6 reveal-up stagger-${i + 1}`}>
            
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.2)' }}>
                <Icon name={promise.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-gold" style={{ color: 'var(--gold)' }} />
              </div>
              <h3 className="font-serif text-lg font-medium text-white mb-2">{promise.title}</h3>
              <p className="text-sm leading-relaxed font-sans" style={{ color: 'rgba(255,255,255,0.5)' }}>{promise.text}</p>
            </div>
          )}
        </div>
      </div>
    </section>);

}